import { Equipment, EquipmentElem_Plates, EquipmentType } from "@cf-shared/data-model/equipment";
import { FoodMeasure, LABEL_SERVING_MEASURE_NAME, Nutrients, NutrientTargets } from "@cf-shared/data-model/food";
import { Log_WorkoutExercise, Log_WorkoutSet } from "@cf-shared/data-model/log-workout";
import { RECIPE_SERVINGS_MEASURE_NAME } from "@cf-shared/data-model/recipe";
import { LocaleSettings, roundToPrecision, toFraction } from "@cf-shared/shared-util";
import { MeasureUnitConfig, MeasurementType } from "@cf-shared/measure-configs";
// The pure measurement formatter now lives in shared/ so the server can render a
// logged weight the same way the app does (#4903). Re-exported here because this
// file is where the whole client already imports it from.
import { canonValueToTexts_global, dualUnitRatio, findBestUnitConfig, findBestUnitConfigForType, formatMeasure } from "@cf-shared/measure-format";
import { NutrientTexts, buildDefaultDisplayNutrients } from '@cf-shared/data-model/display-nutrient';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { DateTime } from "luxon";
import { union, upperFirst } from "lodash-es";
import { formatShortDateCore, formatTimeCore } from "@cf-shared/date-format";
import { relativeDayLabel } from "@cf-shared/time";
import {
  ExerciseStatState,
  ExerciseStatStateDescription,
  ExerciseStatStateTexts,
  exerciseStatStateDescription,
} from "@cf-shared/data-model/user-state";
import { BfErrorTexts, SkinfoldSite } from "@cf-shared/data-model/bodyfat";
import { AuthRoleDesc, AuthRoleId, AuthRoleTexts, authRoleDescriptionsWithTexts } from "@cf-shared/couch-auth";
import { BOOT_LOCALE, LL } from "i18n/i18n.service";
import type { TranslationFunctions } from "../../gensrc/i18n/i18n-types";
import { SettingsService } from "services/settings.service";
import { UserDb } from "services/user-db.service";
import { TranslatedString, untranslatedString } from "models/translated-string";

export { canonValueToTexts_global, dualUnitRatio, findBestUnitConfig, findBestUnitConfigForType, formatMeasure };

export namespace Formatting {
  export const thinSpace = '\u202F';  // thin non-breaking space ` `
  export const times = thinSpace + '\u00d7' + thinSpace; // "small" times symbol `×`
  export const plus = Formatting.thinSpace + '+' + Formatting.thinSpace;
  /** what `formatExerciseSets` writes between two reps of one weight */
  export const repSeparator = ',' + thinSpace;
  /** what `formatExerciseSets` writes between the sets of one weight and the sets of the next */
  export const setGroupSeparator = '; ';
  export const ellipsis = '\u2026'; // horizontal ellipsis `…`
}

const unitRemapTable = {
  ['ug']: 'µg',
};

// Thin settings-aware wrappers over the pure cores in @cf-shared/date-format.
// The cores take explicit inputs (dateFormat / is24hr / locale) so they stay
// unit-testable; here we supply the UI locale (BOOT_LOCALE) and the user's two
// explicit overrides from settings. #4204.
export function formatTime(time: DateTime, settings: SettingsService): string {
  return formatTimeCore(time, settings.isClock24hr(), BOOT_LOCALE);
}

export function formatShortDate(date: DateTime, settings: SettingsService): string {
  return formatShortDateCore(date, settings.dateFormat, BOOT_LOCALE);
}

/**
 * "today" / "yesterday" / "tomorrow", or a date, relative to `today` (#4317).
 *
 * The shared core takes the locale AND the three near-day words explicitly, so
 * it stays pure and unit-testable. This wrapper is the only place that supplies
 * them, exactly like `formatTime` and `formatShortDate` above.
 *
 * The words come back LOWERCASE, because several callers append them mid-phrase
 * ("Breakfast, yesterday"). Callers that start a sentence or a heading with the
 * result capitalise it with `upperFirstTranslated()` below -- NOT lodash's
 * `upperFirst`, which returns a plain `string` and so drops the brand.
 *
 * WHY THE ASSERTION HERE (#4368). Every branch of `relativeDayLabel` returns
 * display-ready text in the user's language: the three near-day words come from
 * the LL catalog, and the other branches come from the locale-aware date
 * formatters in @cf-shared/date-format. But a formatted date is not CATALOG
 * text, so the shared function is honestly typed `string` and cannot hand back
 * the branded type on its own. Asserting once, here, is what lets all nine call
 * sites feed a `TranslatedString` slot directly instead of each wrapping the
 * result in `untranslatedString()` and losing the check.
 */
export function formatRelativeDay(today: DateTime, time: DateTime, ll: TranslationFunctions = LL): TranslatedString {
  return untranslatedString(relativeDayLabel(today, time, BOOT_LOCALE, ll.common.relativeDay));
}

/**
 * `upperFirst` that keeps the "this text is translated" mark.
 *
 * Capitalising the first letter of already-translated text does not make it untranslated, but
 * lodash's `upperFirst` is typed to return a plain `string`, which would silently drop the mark
 * at every call site. Doing it once here keeps the check intact for callers that start a heading
 * with a lowercase relative-day word (#4368).
 */
export function upperFirstTranslated(text: TranslatedString): TranslatedString {
  return untranslatedString(upperFirst(text));
}

// Auto-generated schedule entries are STORED in the user's database with English
// names — meals as "Meal 1", "Meal 2", …, workouts as "Workout A". That is
// deliberate: the stored value is the stable canonical form, so it stays
// comparable across languages and needs no data migration when someone switches
// language. The translation therefore happens at DISPLAY time, right here.
//
// Both helpers are the ONE place that knows this convention. Before #4317 the
// meal regex was copy-pasted into three call sites and one of them (the meal
// detail page) simply forgot it, which is why the list said "Comida 3" and the
// detail header said "Meal 3" for the very same meal.
//
// A name the USER typed is normally left untouched, because it does not look
// like a generated one. Note the workout pattern is deliberately loose (the plan
// letter can be any short label), so a hand-written name that literally begins
// with "Workout " WOULD be re-templated. That is harmless today — nothing in the
// UI lets a user name a logged workout — but do not read the pattern as a
// guarantee that user text is safe. The meal pattern, anchored on digits, is exact.
const GENERATED_MEAL_NAME = /^Meal (\d+)$/;
const GENERATED_WORKOUT_NAME = /^Workout (.+)$/;

// Both helpers take the translation object as an optional last argument,
// defaulting to the process-global `LL`, exactly like `describeEquipment` below.
// Production code never passes it; tests do, so the TRANSLATING branch can be
// checked against a non-English locale. Without that seam the English-bound test
// process makes "Meal 3" -> "Meal 3", which would stay green even if the
// substitution were deleted.

/** Display name for a scheduled meal: "Meal 3" -> "Comida 3", "Breakfast" -> "Breakfast". */
export function formatScheduledMealName(storedName: string | undefined, ll: TranslationFunctions = LL): string {
  const match = storedName?.match(GENERATED_MEAL_NAME);
  return match ? ll.dayItems.titles.meal({ number: match[1] }) : (storedName ?? '');
}

/** Display name for a workout: "Workout A" -> "Entrenamiento A". The letter is from the plan. */
export function formatWorkoutName(storedName: string | undefined, ll: TranslationFunctions = LL): string {
  const match = storedName?.match(GENERATED_WORKOUT_NAME);
  return match ? ll.dayItems.titles.workoutNamed({ name: match[1] }) : (storedName ?? '');
}

/**
 * The user's language name for a weekday, taking Luxon's numbering where 1 is
 * Monday and 7 is Sunday. A number outside 1-7 gives an empty string.
 *
 * This is the one copy of the list. Before it existed the same array was built in
 * four places, and #4687 found one copy that was never translated and was indexed
 * by the wrong number while the others were correct.
 */
export function weekdayName(weekday: number, ll: TranslationFunctions = LL): TranslatedString {
  const weekdayNames = [
    ll.common.weekdays.monday(),
    ll.common.weekdays.tuesday(),
    ll.common.weekdays.wednesday(),
    ll.common.weekdays.thursday(),
    ll.common.weekdays.friday(),
    ll.common.weekdays.saturday(),
    ll.common.weekdays.sunday(),
  ];
  return weekdayNames[weekday - 1] ?? untranslatedString('');
}

/**
 * The single-letter weekday initials in the user's language, Monday first (index 0) to Sunday
 * (index 6). The one copy of this list, for the same reason `weekdayName` is the one copy of its.
 */
export function weekdayInitialsMondayFirst(ll: TranslationFunctions = LL): TranslatedString[] {
  return [
    ll.common.weekdayInitials.monday(),
    ll.common.weekdayInitials.tuesday(),
    ll.common.weekdayInitials.wednesday(),
    ll.common.weekdayInitials.thursday(),
    ll.common.weekdayInitials.friday(),
    ll.common.weekdayInitials.saturday(),
    ll.common.weekdayInitials.sunday(),
  ];
}

export function shortUnitName(longName: string) {
  // remove everything after first open bracket, or after first comma, or semicolon
  let shortName = longName.match(/^[^,(;]*/)[0];
  shortName = shortName && shortName.trim();

  // if the remaining part still has more than one word
  const words = shortName ? shortName.split(' ') : [];
  for (let iWord = 0; iWord < words.length; iWord++) {
    // check if word is number
    if (words[iWord].match(/^\d+$/)) {
      continue;
    }
    shortName = words.slice(0, iWord + 1).join(' ');
    if (iWord < words.length - 1) {
      shortName += Formatting.ellipsis;
    }
    break;
  }

  // if nothing was removed, then we don't need to specially treat this for shortening
  if (!shortName || shortName == longName) {
    shortName = undefined;
  }

  return shortName;
}

// maps each built-in measure name to a function that resolves its translated display label
// against a given translation object. A stored FoodMeasure.name must stay this untranslated
// English canonical value forever (it is compared against, e.g. `measure.name === 'prepared'`,
// and it is what gets saved to the database) -- only the text shown to the user gets
// translated, via translateMeasureName below, never the underlying value.
//
// This is the ONE place the set of built-in measure names is written down. measures-list.ts's
// picker list is DERIVED from PICKABLE_MEASURE_NAMES below (not hand-copied), so the two can
// never drift the way a hand-maintained second copy could -- a name added here is
// automatically pickable, and nothing needs updating in two files at once.
//
// The one exception is the recipe servings name, which this map still translates but
// PICKABLE_MEASURE_NAMES deliberately leaves out of the picker -- see its comment below.
//
// Each entry takes `ll` as a parameter (instead of the map being pre-bound to the process-global
// LL) so a test can pass a non-English translation object and prove the substitution actually
// happens -- every English measure name is a single word, so an English-only test would stay
// green even if translateMeasureName silently returned its input unchanged. Same seam as
// formatScheduledMealName / formatWorkoutName above.
const MEASURE_NAME_TRANSLATORS: Record<string, (ll: TranslationFunctions) => TranslatedString> = {
  portion: ll => ll.common.measureNames.portion(),
  piece: ll => ll.common.measureNames.piece(),
  slice: ll => ll.common.measureNames.slice(),
  whole: ll => ll.common.measureNames.whole(),
  prepared: ll => ll.common.measureNames.prepared(),
  large: ll => ll.common.measureNames.large(),
  medium: ll => ll.common.measureNames.medium(),
  small: ll => ll.common.measureNames.small(),
  can: ll => ll.common.measureNames.can(),
  bottle: ll => ll.common.measureNames.bottle(),
  quart: ll => ll.common.measureNames.quart(),
  pint: ll => ll.common.measureNames.pint(),
  cup: ll => ll.common.measureNames.cup(),
  tbsp: ll => ll.common.measureNames.tbsp(),
  tablespoon: ll => ll.common.measureNames.tablespoon(),
  tsp: ll => ll.common.measureNames.tsp(),
  teaspoon: ll => ll.common.measureNames.teaspoon(),
};

// every built-in measure name. This is the set translateMeasureName knows how to translate,
// so it must keep listing names that are only ever written by the app itself.
export const BUILT_IN_MEASURE_NAMES: readonly string[] = Object.keys(MEASURE_NAME_TRANSLATORS);

// the built-in names a user may actually CHOOSE, in the order the picker should list them.
// buildMeasurePickerNames below is what the picker actually reads; this array is the built-in
// part of what it offers, and measures-list.ts takes its default name for a new row from it.
//
// RECIPE_SERVINGS_MEASURE_NAME is deliberately left out (#4380). It is an internal
// recipe-only name: a recipe's single measure is always called that, and its `amount` is how
// many servings the recipe makes. Nothing else may carry it -- edit-food's save path strips
// such a measure off every non-recipe food. So offering it in the picker only ever gave a
// user a way to make the measure they had just filled in disappear without being told.
export const PICKABLE_MEASURE_NAMES: readonly string[] =
  BUILT_IN_MEASURE_NAMES.filter(name => name !== RECIPE_SERVINGS_MEASURE_NAME);

// True for a measure name the app writes for itself, which a user must never be able to choose
// and which must never be registered as one of their own reusable names.
//
// There are two such names, and the measures editor refuses to let the user edit either one:
// RECIPE_SERVINGS_MEASURE_NAME, which a recipe shows as fixed text rather than a picker
// (#4380), and LABEL_SERVING_MEASURE_NAME, whose row the measures card hides entirely because
// the US nutrition label's serving size is edited by its own field further up the page (#4722).
// Offering either in the picker only ever gave the user a way to make the measure they had
// just filled in disappear, with nothing said.
export function isReservedMeasureName(name: string): boolean {
  return name === RECIPE_SERVINGS_MEASURE_NAME || name === LABEL_SERVING_MEASURE_NAME;
}

// Builds the list of names the measure-name picker offers: the names the user has created on
// their own foods, plus every built-in name a user may choose, plus the names the food being
// edited already carries -- minus the reserved names above.
//
// That third source is why the caller cannot just use PICKABLE_MEASURE_NAMES: a picker whose
// options do not include the row's current value falls back to showing that value raw and
// untranslated (cs-select.ts's getLabel), so a name already on the food has to be offered even
// when it is not a built-in one.
//
// It is also why filtering here is needed on top of not registering the reserved names in the
// first place (edit-food.ts's save). A US-style food really does carry a measure named
// LABEL_SERVING_MEASURE_NAME, and a user whose saved name list picked one up before the fix
// still has it stored -- so both of those would put the name back into the picker.
export function buildMeasurePickerNames(
  savedCustomNames: readonly string[],
  namesOnThisFood: readonly string[],
): string[] {
  const allNames = union([...savedCustomNames], [...PICKABLE_MEASURE_NAMES], [...namesOnThisFood]);
  return allNames.filter(name => !isReservedMeasureName(name));
}

// true for a canonical name that MEASURE_NAME_TRANSLATORS actually owns -- checked with
// hasOwnProperty (not the `in` operator) so a custom measure a user typed as "constructor",
// "toString" or another Object.prototype member name is never mistaken for a built-in one
export function isBuiltInMeasureName(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(MEASURE_NAME_TRANSLATORS, name);
}

// translates a built-in measure name (from MEASURE_NAME_TRANSLATORS) for display; a custom
// measure name the user typed themselves is not catalog content, so it passes through
// untranslated
export function translateMeasureName(name: string, ll: TranslationFunctions = LL): TranslatedString {
  return isBuiltInMeasureName(name) ? MEASURE_NAME_TRANSLATORS[name](ll) : untranslatedString(name);
}

// Unit-picker descriptions (df-b0427dd3fe59). MeasureUnitConfig.description held English literals
// ('Generic number', 'kilograms', 'kilocalories', ...) that render as the help tooltip behind the
// unit picker's help icon (cs-select-popover) and as the label fallback -- so a non-English user
// read them in English. Each config now carries a stable descriptionKey and the localized text is
// looked up here.
//
// Keyed by MeasureUnitConfig.descriptionKey, NOT by .id: several configs share an id with
// DIFFERENT descriptions (kcal/kJ both have id 'kcal') and Config_None has no id, so an id-keyed
// map would collide. Same ll-taking shape and testability seam as MEASURE_NAME_TRANSLATORS above.
const MEASURE_DESCRIPTION_TRANSLATORS: Record<string, (ll: TranslationFunctions) => TranslatedString> = {
  none: ll => ll.common.measureUnits.descriptions.none(),
  genericNumber: ll => ll.common.measureUnits.descriptions.genericNumber(),
  repetitions: ll => ll.common.measureUnits.descriptions.repetitions(),
  sets: ll => ll.common.measureUnits.descriptions.sets(),
  pairs: ll => ll.common.measureUnits.descriptions.pairs(),
  plates: ll => ll.common.measureUnits.descriptions.plates(),
  percent: ll => ll.common.measureUnits.descriptions.percent(),
  millimeters: ll => ll.common.measureUnits.descriptions.millimeters(),
  centimeters: ll => ll.common.measureUnits.descriptions.centimeters(),
  feetAndInches: ll => ll.common.measureUnits.descriptions.feetAndInches(),
  inches: ll => ll.common.measureUnits.descriptions.inches(),
  kilograms: ll => ll.common.measureUnits.descriptions.kilograms(),
  pounds: ll => ll.common.measureUnits.descriptions.pounds(),
  micrograms: ll => ll.common.measureUnits.descriptions.micrograms(),
  milligrams: ll => ll.common.measureUnits.descriptions.milligrams(),
  grams: ll => ll.common.measureUnits.descriptions.grams(),
  ounces: ll => ll.common.measureUnits.descriptions.ounces(),
  stonesAndPounds: ll => ll.common.measureUnits.descriptions.stonesAndPounds(),
  kilocalories: ll => ll.common.measureUnits.descriptions.kilocalories(),
  kilojoules: ll => ll.common.measureUnits.descriptions.kilojoules(),
  internationalUnits: ll => ll.common.measureUnits.descriptions.internationalUnits(),
  milliliters: ll => ll.common.measureUnits.descriptions.milliliters(),
  fluidOunces: ll => ll.common.measureUnits.descriptions.fluidOunces(),
  quarts: ll => ll.common.measureUnits.descriptions.quarts(),
  pints: ll => ll.common.measureUnits.descriptions.pints(),
  cups: ll => ll.common.measureUnits.descriptions.cups(),
  tablespoons: ll => ll.common.measureUnits.descriptions.tablespoons(),
  teaspoons: ll => ll.common.measureUnits.descriptions.teaspoons(),
};

// translates a unit config's description for display in the unit picker. A config whose
// descriptionKey is unset or unmapped falls back to its raw English description -- checked with
// hasOwnProperty (not `in`) so a key that collides with an Object.prototype member is never
// mistaken for a built-in one.
export function translateMeasureDescription(config: MeasureUnitConfig, ll: TranslationFunctions = LL): TranslatedString {
  const key = config.descriptionKey;
  return key && Object.prototype.hasOwnProperty.call(MEASURE_DESCRIPTION_TRANSLATORS, key)
    ? MEASURE_DESCRIPTION_TRANSLATORS[key](ll)
    : untranslatedString(config.description);
}

// Supplies the exercise-state badge words to the shared builder in
// @cf-shared/data-model/user-state. The shared module cannot import LL, so it takes the words as
// a parameter; because that parameter is generic, passing LL here is what makes the result come
// back branded as TranslatedString instead of plain string (#4368).
//
// Like MEASURE_NAME_TRANSLATORS above, each entry takes `ll` rather than closing over the
// process-global LL, so a test can drive it with a Spanish translation object and prove the
// words really are translated.
function exerciseStateTexts(ll: TranslationFunctions): {
  [state in ExerciseStatState]: ExerciseStatStateTexts<TranslatedString>;
} {
  return {
    active: { title: () => ll.exerciseStates.active.title(), description: () => ll.exerciseStates.active.description() },
    plateaued: { title: () => ll.exerciseStates.plateaued.title(), description: () => ll.exerciseStates.plateaued.description() },
    unsuitable: { title: () => ll.exerciseStates.unsuitable.title(), description: () => ll.exerciseStates.unsuitable.description() },
    avoided: { title: () => ll.exerciseStates.avoided.title(), description: () => ll.exerciseStates.avoided.description() },
    ready: { title: () => ll.exerciseStates.ready.title(), description: () => ll.exerciseStates.ready.description() },
    unused: { title: () => ll.exerciseStates.unused.title(), description: () => ll.exerciseStates.unused.description() },
  };
}

/**
 * Describes one exercise state for a badge, in the user's language.
 *
 * Use the returned object's `state` field to branch on which state it is -- never its `title`,
 * which is translated text.
 */
export function describeExerciseState(
  state: ExerciseStatState,
  ll: TranslationFunctions = LL,
): ExerciseStatStateDescription<TranslatedString> {
  return exerciseStatStateDescription(state, exerciseStateTexts(ll));
}

// Supplies the role words to the shared builder in @cf-shared/couch-auth, same seam as
// exerciseStateTexts above. The shared role table itself is text-free, because the server and
// tools/ resolve roles by id/value and never display them (#4368).
function authRoleTexts(ll: TranslationFunctions): { [roleId in AuthRoleId]: AuthRoleTexts<TranslatedString> } {
  return {
    unknown: { name: () => ll.authRoles.unknown.name(), userExplanation: () => ll.authRoles.unknown.userExplanation() },
    paid: { name: () => ll.authRoles.paid.name(), userExplanation: () => ll.authRoles.paid.userExplanation() },
    vip: { name: () => ll.authRoles.vip.name(), userExplanation: () => ll.authRoles.vip.userExplanation() },
    mod: { name: () => ll.authRoles.mod.name(), userExplanation: () => ll.authRoles.mod.userExplanation() },
    dev: { name: () => ll.authRoles.dev.name(), userExplanation: () => ll.authRoles.dev.userExplanation() },
    officer: { name: () => ll.authRoles.officer.name(), userExplanation: () => ll.authRoles.officer.userExplanation() },
    serverdev: { name: () => ll.authRoles.serverdev.name(), userExplanation: () => ll.authRoles.serverdev.userExplanation() },
  };
}

/** All subscription roles with their names and explanations in the user's language. */
export function describeAuthRoles(ll: TranslationFunctions = LL): AuthRoleDesc<TranslatedString>[] {
  return authRoleDescriptionsWithTexts(authRoleTexts(ll));
}

// Supplies the body-fat validation messages to `calculateBF` in @cf-shared/data-model/bodyfat.
// Same seam as the two text adapters above (#4368).
export function bfErrorTexts(ll: TranslationFunctions = LL): BfErrorTexts<TranslatedString> {
  return {
    missingSkinfold: skinfold => ll.bodyfatInput.errors.missingSkinfold({ skinfold }),
    // same source as the site HEADINGS, so the message can never name a site differently
    // from the heading above the input it is complaining about
    skinfoldNames: {
      chest: () => skinfoldSiteName('chest', ll),
      tricep: () => skinfoldSiteName('tricep', ll),
      umbilical: () => skinfoldSiteName('umbilical', ll),
      thigh: () => skinfoldSiteName('thigh', ll),
      suprailiac: () => skinfoldSiteName('suprailiac', ll),
    },
    genderRequired: () => ll.bodyfatInput.errors.genderRequired(),
    tapeAllValues: () => ll.bodyfatInput.errors.tapeAllValues(),
    tapeInvalid: () => ll.bodyfatInput.errors.tapeInvalid(),
    noMethod: () => ll.bodyfatInput.errors.noMethod(),
    valuesOutOfRange: () => ll.bodyfatInput.errors.valuesOutOfRange(),
  };
}

// Nutrient display names (#4368). These were English literals in
// @cf-shared/data-model/display-nutrient until this change; they are ordinary UI copy, so they
// now live in the catalog like every other word the user reads. The shared module takes the
// lookup as a parameter, which is what lets the branded TranslatedString survive the trip.
//
// Keyed by INFOODS tag, valued by an `ll`-taking function -- the same shape (and the same
// testability reason) as MEASURE_NAME_TRANSLATORS above.
const NUTRIENT_NAME_TRANSLATORS: Record<string, (ll: TranslationFunctions) => TranslatedString> = {
  ["ENER$_kcal"]: ll => ll.nutrients.names.energy(),
  ["PRO$_g"]: ll => ll.nutrients.names.protein(),
  ["FAT_g"]: ll => ll.nutrients.names.fat(),
  ["CHOAVL_g"]: ll => ll.nutrients.names.carbohydrates(),
  ["FIB$_g"]: ll => ll.nutrients.names.fiber(),
  ["CHOLE_mg"]: ll => ll.nutrients.names.cholesterol(),
  ["FASAT_g"]: ll => ll.nutrients.names.fatSaturated(),
  ["FAMS_g"]: ll => ll.nutrients.names.fatMonounsaturated(),
  ["FAPU_g"]: ll => ll.nutrients.names.fatPolyunsaturated(),
  ["FAPUN3_g"]: ll => ll.nutrients.names.omega3(),
  ["FAPUN6_g"]: ll => ll.nutrients.names.omega6(),
  ["FAPUN9_g"]: ll => ll.nutrients.names.omega9(),
  ["FATRN_g"]: ll => ll.nutrients.names.transsaturatedFat(),
  ["CHOCDF_g"]: ll => ll.nutrients.names.carbohydratesTotal(),
  ["SUGAR_g"]: ll => ll.nutrients.names.sugarsTotal(),
  ["SUGAD_g"]: ll => ll.nutrients.names.sugarAdded(),
  ["STARCH_g"]: ll => ll.nutrients.names.starch(),
  ["NACL_mg"]: ll => ll.nutrients.names.saltNacl(),
  ["ALC_g"]: ll => ll.nutrients.names.alcohol(),
  ["WATER_g"]: ll => ll.nutrients.names.water(),
  ["CAFFN_mg"]: ll => ll.nutrients.names.caffeine(),
  ["THEBRN_mg"]: ll => ll.nutrients.names.theobromine(),
  ["CA_mg"]: ll => ll.nutrients.names.calciumCa(),
  ["FE_mg"]: ll => ll.nutrients.names.ironFe(),
  ["MG_mg"]: ll => ll.nutrients.names.magnesiumMg(),
  ["P_mg"]: ll => ll.nutrients.names.phosphorusP(),
  ["K_mg"]: ll => ll.nutrients.names.potassiumK(),
  ["NA_mg"]: ll => ll.nutrients.names.sodiumNa(),
  ["ZN_mg"]: ll => ll.nutrients.names.zincZn(),
  ["CU_mg"]: ll => ll.nutrients.names.copperCu(),
  ["FLD_ug"]: ll => ll.nutrients.names.fluorideF(),
  ["MN_mg"]: ll => ll.nutrients.names.manganeseMn(),
  ["SE_ug"]: ll => ll.nutrients.names.seleniumSe(),
  ["ID_ug"]: ll => ll.nutrients.names.iodineI(),
  ["VITA_IU_IU"]: ll => ll.nutrients.names.vitaminAIu(),
  ["RETOL_ug"]: ll => ll.nutrients.names.retinol(),
  ["VITA_RAE_ug"]: ll => ll.nutrients.names.vitaminARae(),
  ["CARTB_ug"]: ll => ll.nutrients.names.caroteneBeta(),
  ["CARTA_ug"]: ll => ll.nutrients.names.caroteneAlpha(),
  ["TOCPHA_mg"]: ll => ll.nutrients.names.vitaminEAlphaTocopherol(),
  ["VITD_ug"]: ll => ll.nutrients.names.vitaminD(),
  ["ERGCAL_ug"]: ll => ll.nutrients.names.vitaminD2Ergocalciferol(),
  ["CHOCAL_ug"]: ll => ll.nutrients.names.vitaminD3Cholecalciferol(),
  ["VITC_mg"]: ll => ll.nutrients.names.vitaminCTotalAscorbicAcid(),
  ["THIA_mg"]: ll => ll.nutrients.names.thiaminVitaminB1(),
  ["RIBF_mg"]: ll => ll.nutrients.names.riboflavinVitaminB2(),
  ["NIA_mg"]: ll => ll.nutrients.names.niacinVitaminB3(),
  ["PANTAC_mg"]: ll => ll.nutrients.names.pantothenicAcid(),
  ["VITB6A_mg"]: ll => ll.nutrients.names.vitaminB6(),
  ["FOL_ug"]: ll => ll.nutrients.names.folateTotal(),
  ["VITB12_ug"]: ll => ll.nutrients.names.vitaminB12(),
  ["CHOLN_mg"]: ll => ll.nutrients.names.cholineTotal(),
  ["VITK1_ug"]: ll => ll.nutrients.names.vitaminKPhylloquinone(),
  ["FOLAC_ug"]: ll => ll.nutrients.names.folicAcid(),
  ["ASH_g"]: ll => ll.nutrients.names.ash(),
  ["BIOT_ug"]: ll => ll.nutrients.names.biotinVitaminH(),
  ["CR_ug"]: ll => ll.nutrients.names.chromiumCr(),
  ["MO_ug"]: ll => ll.nutrients.names.molybdenumMo(),
  ["F18D2CN6_g"]: ll => ll.nutrients.names.fattyAcidsLinoleic(),
  ["F18D3N3_g"]: ll => ll.nutrients.names.fattyAcidsAlphaLinolenic(),
  ["F20D5_g"]: ll => ll.nutrients.names.epa(),
  ["F22D6N3_g"]: ll => ll.nutrients.names.dha(),
  ["F20D4_g"]: ll => ll.nutrients.names.fattyAcidsArachidonic(),
  ["SORTL_mg"]: ll => ll.nutrients.names.sorbitol(),
  ["MALTL_mg"]: ll => ll.nutrients.names.maltitol(),
  ["MANIT_mg"]: ll => ll.nutrients.names.mannitol(),
  ["ISOMT_mg"]: ll => ll.nutrients.names.isomalt(),
  ["LACTIT_mg"]: ll => ll.nutrients.names.lactitol(),
  ["XILIT_mg"]: ll => ll.nutrients.names.xylitol(),
  ["ERITR_mg"]: ll => ll.nutrients.names.erythritol(),
  ["POLYL_mg"]: ll => ll.nutrients.names.polyolsSugarAlcoholsTotal(),
};

// Abbreviated forms. Only the handful of nutrients shown on the macro cards have these; every
// other nutrient falls back to its full name.
const NUTRIENT_SHORT_NAME_TRANSLATORS: Record<string, (ll: TranslationFunctions) => TranslatedString> = {
  ["ENER$_kcal"]: ll => ll.nutrients.short.energy(),
  ["PRO$_g"]: ll => ll.nutrients.short.protein(),
  ["FAT_g"]: ll => ll.nutrients.short.fat(),
  ["CHOAVL_g"]: ll => ll.nutrients.short.carbohydrates(),
  ["FIB$_g"]: ll => ll.nutrients.short.fiber(),
  ["CHOLE_mg"]: ll => ll.nutrients.short.cholesterol(),
};

const NUTRIENT_THREE_LETTER_NAME_TRANSLATORS: Record<string, (ll: TranslationFunctions) => TranslatedString> = {
  ["ENER$_kcal"]: ll => ll.nutrients.threeLetter.energy(),
  ["PRO$_g"]: ll => ll.nutrients.threeLetter.protein(),
  ["FAT_g"]: ll => ll.nutrients.threeLetter.fat(),
  ["CHOAVL_g"]: ll => ll.nutrients.threeLetter.carbohydrates(),
  ["FIB$_g"]: ll => ll.nutrients.threeLetter.fiber(),
};

// Looks a tag up in one of the maps above. Uses hasOwnProperty rather than the `in` operator so a
// tag that happens to match an Object.prototype member name ("constructor", "toString") is not
// mistaken for a real entry -- same guard as isBuiltInMeasureName.
function lookupNutrientText(
  translators: Record<string, (ll: TranslationFunctions) => TranslatedString>,
  tag: string,
  ll: TranslationFunctions,
): TranslatedString | undefined {
  return Object.prototype.hasOwnProperty.call(translators, tag) ? translators[tag](ll) : undefined;
}

/**
 * The display name for one nutrient, in the user's language.
 *
 * A tag with no catalog entry falls back to the tag itself. That is not expected -- every tag in
 * NutrientDefinitionsOrdered has a name -- but a food database row could carry an unknown tag,
 * and showing the raw tag is more useful to a future debugger than showing nothing.
 */
export function nutrientName(tag: string, ll: TranslationFunctions = LL): TranslatedString {
  return lookupNutrientText(NUTRIENT_NAME_TRANSLATORS, tag, ll) ?? untranslatedString(tag);
}

/** Supplies the nutrient words to the shared builder in @cf-shared/data-model/display-nutrient. */
export function nutrientTexts(ll: TranslationFunctions = LL): NutrientTexts<TranslatedString> {
  return {
    name: tag => nutrientName(tag, ll),
    shortName: tag => lookupNutrientText(NUTRIENT_SHORT_NAME_TRANSLATORS, tag, ll),
    threeLetterName: tag => lookupNutrientText(NUTRIENT_THREE_LETTER_NAME_TRANSLATORS, tag, ll),
  };
}

// The very short macro labels on the inline-compact card -- the tightest layout there is, where
// only room for a letter or two exists. These are NOT initials of the English words: a language
// abbreviates its OWN term, so French fat is `L` (lipides) and French carbs are `G` (glucides).
// Dutch takes a three-letter `Vez` for fiber because *vet* and *vezels* would both be `V`, the
// same concession English already makes with `Fib`.
const NUTRIENT_ONE_LETTER_TRANSLATORS: Record<string, (ll: TranslationFunctions) => TranslatedString> = {
  ["PRO$_g"]: ll => ll.nutrientList.oneLetter.protein(),
  ["CHOAVL_g"]: ll => ll.nutrientList.oneLetter.carbs(),
  ["FAT_g"]: ll => ll.nutrientList.oneLetter.fat(),
  ["FIB$_g"]: ll => ll.nutrientList.oneLetter.fiber(),
};

/**
 * The shortest label for a macro on the inline-compact card -- one to three characters, since a
 * language that would otherwise collide needs more than a letter (see the map above). Returns
 * `undefined` for a nutrient with no such form, and the caller falls back to its own abbreviation.
 */
export function nutrientOneLetterLabel(tag: string, ll: TranslationFunctions = LL): TranslatedString | undefined {
  return lookupNutrientText(NUTRIENT_ONE_LETTER_TRANSLATORS, tag, ll);
}

// The five skinfold measurement sites, keyed by the SkinfoldSite union shared/ already defines.
//
// This map is the ONE place a skinfold site name is resolved. It exists because there are two
// separate skinfold UIs -- the shared `bodyfat-input` component (weekly checkup + intake form)
// and `edit-weigh-in`'s own per-gender lists -- and they drifted: one translated its names and
// the other did not, so the same screen showed a translated error message under an English
// heading. Both now read from here, so they cannot disagree again.
const SKINFOLD_SITE_NAME_TRANSLATORS: { [site in SkinfoldSite]: (ll: TranslationFunctions) => TranslatedString } = {
  chest: ll => ll.editWeighIn.skinfolds.chest(),
  tricep: ll => ll.editWeighIn.skinfolds.tricep(),
  suprailiac: ll => ll.editWeighIn.skinfolds.suprailiac(),
  umbilical: ll => ll.editWeighIn.skinfolds.umbilical(),
  thigh: ll => ll.editWeighIn.skinfolds.thigh(),
};

/** The display name for one skinfold measurement site, in the user's language. */
export function skinfoldSiteName(site: SkinfoldSite, ll: TranslationFunctions = LL): TranslatedString {
  return SKINFOLD_SITE_NAME_TRANSLATORS[site](ll);
}

export interface FormatServingSizeOptions {
  weightUnit?: string;
  useFullName?: boolean;
}

/**
 * Formats a food measure into a display string like "1 cup | 240 g"
 * @param measure The food measure to format
 * @param locale Locale settings for number formatting
 * @param opts Options: weightUnit (defaults to 'g'), useFullName (defaults to false)
 * @param ll Translation object for the measure name, defaults to the process-global LL (tests
 * pass a non-English one to prove the measure name is actually translated, not just formatted)
 */
export function formatServingSize(
  measure: FoodMeasure,
  locale: LocaleSettings,
  opts?: FormatServingSizeOptions,
  ll: TranslationFunctions = LL,
): string {
  const parts: string[] = [];
  const { weightUnit, useFullName } = opts || {};

  // if measure has a name, show "amount name"
  if (measure.name) {
    const amount = measure.amount ?? 1;
    const translatedName = translateMeasureName(measure.name, ll);
    // shortUnitName truncates a long value to its first word -- built-in measure names are
    // catalog text whose length is controlled by the translation itself (many translated names
    // are multiple words, e.g. es "cuarto de galón" for `quart`), so they must always render
    // whole. Only a user-typed custom measure name is a candidate for shortening.
    const displayName = (useFullName || isBuiltInMeasureName(measure.name))
      ? translatedName
      : (shortUnitName(translatedName) || translatedName);
    // format amount as fraction if possible, otherwise round to 1 decimal
    const formattedAmount = toFraction(amount, { maxDenominator: 8, makeBallpark: true })
      ?? roundToPrecision(amount, 0.1).toString();
    parts.push(`${formattedAmount} ${displayName}`);
  }

  // if weight is provided, format it properly using the specified unit
  if (measure.weight_g !== undefined) {
    const unit = weightUnit || 'g';
    const formattedWeight = formatMeasure(measure.weight_g, 'foodweight_g', locale, unit);
    parts.push(formattedWeight);
  }

  return parts.join(' | ');
}

/** This is similar to formatMeasure(), but it doesn't take a value and generate a string.
 * It instead just gives back the factor needed to convert the canonical value to the user's preferred unit.
 * 
 * It is useful for e.g. graphs where we need to pass the number to a 3rd party library.
 * 
 * This always uses the decimals of the primary unit. Eventual secondary unit (e.g pounds for stones & pounds) is not used.
 */
export function getMeasureUnitConversion(type: string | MeasurementType, locale: LocaleSettings, unit: string): { factor: number, unitName: string } {
  let { measurementType, measurementUnitConfig } = findBestUnitConfigForType(type, locale, unit);
  return {
    factor: measurementType.nominalUnit / measurementUnitConfig.value1, unitName: measurementUnitConfig.unit1
  };
}

// includePlannedWeight: false leaves out the "[planned weight]" brackets after an overridden weight
// (the compact finished-exercise summary, #4142 follow-up); every other caller keeps them
export function formatExerciseSets(e: Log_WorkoutExercise, locale: LocaleSettings, includeMyoReps: boolean = true, includePlannedWeight: boolean = true) {
  if (!e || !e.sets || e.sets.length <= 0) {
    return 'N/A';
  }
  let lastWeight: number = null;
  let text = '';

  let addMiniReps = (set: Log_WorkoutSet) => {
    if (!includeMyoReps) return;
    if (set.miniSets && set.miniSets.length > 0) {
      for (let miniSet of set.miniSets) {
        text += `${Formatting.plus}${miniSet.reps}`;
      }
    }
  }

  for (let set of e.sets) {
    if (!set || !set.stats || set.stats.reps == null || set.stats.weight_kg == null) {
      continue;
    }
    if (set.stats.weight_kg === lastWeight) {
      text += `${Formatting.repSeparator}${set.stats.reps}`;
      addMiniReps(set);
    } else {
      if (text !== '') {
        text += Formatting.setGroupSeparator;
      }

      text += `${formatMeasure(set.stats.weight_kg, 'addedweight_kg', locale, set.stats.weight_unit)}`;
      if (includePlannedWeight && set.plannedWeight_kg != null && set.plannedWeight_kg !== set.stats.weight_kg) {
        text += `[${formatMeasure(set.plannedWeight_kg, 'addedweight_kg', locale, set.stats.weight_unit)}]`;
      }
      text += `${Formatting.times}${set.stats.reps}`;
      addMiniReps(set);
      lastWeight = set.stats.weight_kg;
    }
  }
  return text;

};

export function formatOneExerciseSet(set: Log_WorkoutSet, locale: LocaleSettings): string {
  if (!set || !set.stats || set.stats.reps == null || set.stats.weight_kg == null) {
    return '';
  }

  let text = `${formatMeasure(set.stats.weight_kg, 'addedweight_kg', locale, set.stats.weight_unit)}${Formatting.times}${set.stats.reps}`;
  if (set.miniSets && set.miniSets.length > 0) {
    for (let miniSet of set.miniSets) {
      text += `${Formatting.plus}${miniSet.reps}`;
    }
  }

  if (set.plannedWeight_kg != null && set.plannedWeight_kg !== set.stats.weight_kg) {
    text += ` recommended:${Formatting.thinSpace}${formatMeasure(
      set.plannedWeight_kg,
      'addedweight_kg',
      locale,
      set.stats.weight_unit,
    )}${Formatting.thinSpace}!`;
  }
  return text;
};

export function FormatNutrients(
  nutrients: Nutrients,
  userDb?: UserDb, // this is required if the opts specifies targets - so that we can check if we are tracking nutrients and/or targets
  opts?: {
    ref_g?: number /** reference weight that "nutrients" object is expressed in (defaults to 100 g)*/;
    actual_g?: number /** actual weight to display the nutrients for (defaults to ref_g) */;
    targets?: NutrientTargets;
    onlyTargets?: boolean /** don't show nutrients, just targets */;
    onlyTracked?: boolean /** only include those that have icons */;
  },
  // the translations to name the nutrients with; production passes nothing and gets the active
  // language, tests pass a specific one to prove the names really are translated (#4368)
  ll: TranslationFunctions = LL,
): UiDisplayNutrient[] {
  // normalize inputs using defaults where not specified
  opts = opts || {};
  let ref_g = opts.ref_g || 100;
  let actual_g = opts.actual_g || ref_g;
  let multiplier = actual_g / ref_g;

  let display = buildDefaultDisplayNutrients(nutrientTexts(ll));
  for (let i = 0; i < display.length; i++) {
    let d = display[i];
    if (opts.onlyTracked && !d.tracked) {
      continue;
    }

    let unit = unitRemapTable[d.unitId] || d.unitId;
    let stored = nutrients[d.tag];
    let value = stored * multiplier;
    d.value = value;
    // a nutrient nobody measured reads as "--" rather than 0: `null` is what an unmeasured
    // nutrient stores, and what the marker "M" is repaired to as a food is read, and
    // `null * multiplier` is 0 -- which would state a measurement the record never made.
    //
    // A caller that hands in an ALREADY-SCALED map must build it with
    // `nutrientsForWeightForDisplay`, which keeps that `null`. `nutrientsForWeight` collapses
    // it to a real 0 for sums, and nothing here could tell the difference.
    if (stored == null || isNaN(Number(value))) {
      d.valueShort = '--';
      d.valueLong = '--';
      d.value = undefined;
    } else {
      d.valueShort = value.toFixed(0);
      d.valueLong = `${value.toFixed(0)}${Formatting.thinSpace}${unit}`;
      if (value < 1 && value > 0) {
        d.valueShort = `<1`
        d.valueLong = `<1${Formatting.thinSpace}${unit}`;
      }
    }
    d.valueUnit = unit;
    let target = opts.targets && opts.targets[d.tag];
    if (target && userDb?.nutritionTargets) {
      let limitText: string;
      let goal: number = target.target || target.min || target.max;

      let valueEff = value || 0;
      d.valueError = ''; // means "display checkmark"

      if (target.target) {
        limitText = `~${target.target.toFixed(0)}`;
        if (valueEff <= target.target - 1) {
          d.valueError = `(-${(target.target - valueEff).toFixed(0)}${Formatting.thinSpace}${unit})`;
        } else if (valueEff >= target.target + 1) {
          d.valueError = `(+${(valueEff - target.target).toFixed(0)}${Formatting.thinSpace}${unit})`;
        }
      } else if (target.min && target.max) {
        limitText = `${target.min.toFixed(0)}-${target.max.toFixed(0)}`;
        if (valueEff <= target.min - 1) {
          d.valueError = `(-${(target.min - valueEff).toFixed(0)}${Formatting.thinSpace}${unit})`;
        } else if (valueEff >= target.max + 1) {
          d.valueError = `(+${(valueEff - target.max).toFixed(0)}${Formatting.thinSpace}${unit})`;
        }
      } else if (target.min) {
        limitText = `>${target.min.toFixed(0)}`;
        if (valueEff <= target.min - 1) {
          d.valueError = `(-${(target.min - valueEff).toFixed(0)}${Formatting.thinSpace}${unit})`;
        }
      } else if (target.max) {
        limitText = `<${target.max.toFixed(0)}`;
        if (valueEff >= target.max + 1) {
          d.valueError = `(+${(valueEff - target.max).toFixed(0)}${Formatting.thinSpace}${unit})`;
        }
      }

      d.target = target;
      d.valueLimit = limitText;
      if (opts.onlyTargets && d.valueLimit) {
        d.valueLimit += ` ${unit}`;
      }
      if (goal != undefined) {
        d.progress_pc = (value / goal) * 100;
      } else {
        d.progress_pc = null;
      }

      // for values that are not specified yet
      if (d.valueShort == '--') {
        d.valueShort = limitText;
        d.limitCSS = 'under';
        // for specified values
      } else {
        if (target.min && value <= target.min - 1) {
          d.limitCSS = 'under';
        } else if (target.max && value >= target.max + 1) {
          d.limitCSS = 'over';
        } else {
          d.limitCSS = 'okay';
        }
      }
    } else {
      d.limitCSS = 'ignored';
    }
  }

  if (opts.onlyTargets) {
    display = display.filter(d => d.valueLimit);
    display.forEach(d => (d.valueLong = null));
  }
  return display as UiDisplayNutrient[];
}

// Build the one-line, human-readable summary of an equipment setup (e.g.
// "20 kg barbell with 2 × 10 kg, 5 kg plates (in pairs)"), fully rendered in the
// user's UI language. Every word comes from the `equipment.summary.*` catalog so
// the sentence never mixes languages (#4292); the weight numbers are formatted
// by the locale-aware formatKg helper. `ll` defaults to the app's global
// translation functions — callers pass it only in tests, to pin an explicit
// locale without depending on the global's load-time binding.
export function describeEquipment(config: Equipment, locale: LocaleSettings, ll: TranslationFunctions = LL): string {
  let formatKg = (kg: number) => formatMeasure(kg, 'addedweight_kg', locale, config.weight_unit);

  let res: string;

  // render a set of plates as a descriptive string, grouping equal weights into
  // "<count> × <weight>" runs (e.g. "2 × 10 kg, 5 kg"), or the "no plates" phrase
  // when the set is empty
  let describePlates = (plates: EquipmentElem_Plates) => {
    if (plates.plates.length <= 0) {
      return ll.equipment.summary.noPlates();
    }

    let plateSets: { count: number, weight_kg: number }[] = [];
    // group plates by weight
    for (let plate of plates.plates) {
      let found = plateSets.find(p => p.weight_kg == plate.weight_kg);
      if (!found) {
        found = { count: 0, weight_kg: plate.weight_kg };
        plateSets.push(found);
      }
      found.count += 1;
    }
    // sort by descending weight
    plateSets.sort((a, b) => b.weight_kg - a.weight_kg);

    // convert that to a descriptive string
    let res = plateSets.map(p => (p.count > 1 ? `${p.count}${Formatting.thinSpace}${Formatting.times}${Formatting.thinSpace}` : '') + formatKg(p.weight_kg)).join(", ");
    return res;
  }

  switch (config.type) {
    case EquipmentType.Manual:
      res = ll.equipment.summary.manual({
        min: formatKg(config.weight_min_kg),
        inc: formatKg(config.weight_inc_kg),
      });
      break;
    case EquipmentType.Bar:
      // the collar clause sits between the bar and the plates, so its word order
      // will not survive translation as a glued fragment — use a whole phrase per case
      if (config.collar_kg) {
        res = ll.equipment.summary.barWithCollars({
          bar: formatKg(config.bar_kg),
          collars: formatKg(config.collar_kg),
          plates: describePlates(config),
        });
      } else {
        res = ll.equipment.summary.bar({
          bar: formatKg(config.bar_kg),
          plates: describePlates(config),
        });
      }
      break;
    case EquipmentType.Fixed: {
      let base = config.fixed.map(f => formatKg(f.weight_kg)).join(", ");
      // if they also have plates, add that as microloading
      if (config.plates && config.plates.length > 0) {
        base = ll.equipment.summary.withMicroloading({ base, plates: describePlates(config) });
      }
      res = base;
      break;
    }
    case EquipmentType.Stack: {
      let base = ll.equipment.summary.stack({
        weights: config.stack.map(s => formatKg(s.totalWeight_kg)).join(', '),
      });
      // if they also have plates, add that as microloading
      if (config.plates && config.plates.length > 0) {
        base = ll.equipment.summary.withMicroloading({ base, plates: describePlates(config) });
      }
      res = base;
      break;
    }
    case EquipmentType.Plates:
      res = ll.equipment.summary.freePlates({ plates: describePlates(config) });
      break;
    default:
      res = ll.equipment.summary.unknown();
      break;
  }

  return res;
}

/**
 * Join already-translated words into one phrase the way the language does: "bone and skin",
 * "peel, seeds and core". Falls back to commas where the browser has no `Intl.ListFormat`.
 */
export function formatList(items: readonly TranslatedString[], locale: string = BOOT_LOCALE): TranslatedString {
  // if the browser cannot join lists for a language, a comma list still reads correctly
  if (typeof Intl === 'undefined' || typeof Intl.ListFormat !== 'function') {
    return untranslatedString(items.join(', '));
  }
  return untranslatedString(new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items));
}
